import OrderDocumentDownload from "@bevco/Components/OrderDocumentDownload";
import OrderTablesTitle from "@bevco/Components/OrderTablesTitle";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import DataTable from "@insite/mobius/DataTable";
import {
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableRow,
    Typography,
} from "@insite/mobius/index";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OrderLines {
    warehouse: string;
}

interface OwnProps {
    erpOrderNumber: string;
    orderDate: string;
    orderLines: OrderLines[];
    guid: string;
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        language: state.context.session.language,
        hideDownloadDocumentButtons: (state.context.settings.settingsCollection as any).myAccountSettings?.hideDownloadDocumentButtons,
    };
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface OrderTableStyles {
    noResults?: InjectableCss;
    dataTableWrapper?: InjectableCss;
}

export const styles: OrderTableStyles = {
    noResults: {
        css: css`
            text-align: center;
        `,
    },
    dataTableWrapper: {
        css: css`
            display: block;
            overflow-x: auto;
            white-space: nowrap;
        `,
    },
};

const OrderAcknowledgement = (props: Props) => {
    useEffect(() => {
        const scrollToHashElement = () => {
            const { hash } = window.location;
            const elementToScroll = document.getElementById(hash?.replace("#", ""));

            if (!elementToScroll) {
                return;
            }

            setTimeout(() => {
                window.scrollTo({
                    top: elementToScroll.offsetTop - 0,
                    behavior: "smooth",
                });
            }, 0);
        };

        if (!props.orderLines) {
            return;
        }

        scrollToHashElement();
        window.addEventListener("hashchange", scrollToHashElement);
        return () => window.removeEventListener("hashchange", scrollToHashElement);
    }, [props.orderLines]);

    const getShippedFromWarehouse = () => {
        const warehouses = (props.orderLines ?? []).map(orderLine => orderLine.warehouse);
        const uniqueWarehouses = [...new Set(warehouses)];

        if (uniqueWarehouses.length === 1) {
            return uniqueWarehouses[0];
        }

        return translate("Multiple DCs");
    };

    return (
        <>
            <OrderTablesTitle text="Order Acknowledgement" />
            {props.erpOrderNumber ? (
                <StyledWrapper {...styles.dataTableWrapper}>
                    <DataTable>
                        <DataTableHead>
                            <DataTableHeader>{translate("Date")}</DataTableHeader>
                            <DataTableHeader>{translate("# Of Lines")}</DataTableHeader>
                            <DataTableHeader>{translate("Shipped From")}</DataTableHeader>
                            <DataTableHeader>{translate("Document #")}</DataTableHeader>
                            {!props.hideDownloadDocumentButtons && (
                                <DataTableHeader>{translate("Download")}</DataTableHeader>
                            )}
                        </DataTableHead>
                        <DataTableBody>
                            <DataTableRow>
                                <DataTableCell>
                                    {getLocalizedDateTime({
                                        dateTime: new Date(props.orderDate),
                                        language: props.language,
                                    })}
                                </DataTableCell>
                                <DataTableCell>{(props.orderLines ?? []).length}</DataTableCell>
                                <DataTableCell>{getShippedFromWarehouse()}</DataTableCell>
                                <DataTableCell>{props.erpOrderNumber}</DataTableCell>
                                {!props.hideDownloadDocumentButtons && (
                                    <DataTableCell>
                                        <OrderDocumentDownload
                                            id={props.guid}
                                            type="order acknowledgement"
                                            name={`order_${props.erpOrderNumber}.pdf`}
                                        />
                                    </DataTableCell>
                                )}
                            </DataTableRow>
                        </DataTableBody>
                    </DataTable>
                </StyledWrapper>
            ) : (
                <Typography {...styles.noResults} as="p" variant="p">
                    {translate("No Documents Available")}
                </Typography>
            )}
        </>
    );
};

export default connect(mapStateToProps)(OrderAcknowledgement);
